import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import useInsightsStore from '../store/Insights';
import type {
  AllChats,
  ChatData,
  ChatObject,
  ChatHistoryItem,
  ChatMetadata,
  ChatProjectKey,
} from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ChatsState {
  allChats: AllChats;
  epicChats: Record<string, any>;
  inputValue: string;
  selectedChatId: string;
  selectedChat: any;
  selectedChatEpic: any;
  powerModePayloadChatId: string;

  getSelectedEpic: () => string;
  initializeChat: (
    chatId?: string | null,
    epicKey?: string | null,
    epicSummary?: string | null,
    projectName?: string | null,
    projectKeys?: ChatProjectKey[],
    dateRange?: string,
    issueUrl?: string | null,
    status?: string | null,
    created?: string | null,
    updated?: string | null,
    mode?: string,
    projectKey?: string | null,
    projectCodeNames?: string[]
  ) => string;
  updateSelectedChat: (selectedChat: ChatData | null) => void;
  updateSelectedChatEpic: (epic: any) => void;
  updatePowerModePayloadChatId: (id: string) => void;
  updateSelectedChatId: (chatId: any) => void;
  getChatConversation: (chatId: string) => ChatObject[];
  getEpicConversation: (chatId: string) => ChatObject[];
  getChatHistory: (chatId: string) => ChatHistoryItem[];
  getChatData: (chatId: string) => ChatData | null;
  getChatMetadata: (chatId: string) => ChatMetadata | null;
  getCurrentConversation: () => ChatObject[];
  getCurrentChatHistory: () => ChatHistoryItem[];
  getCurrentChatMetadata: () => ChatMetadata | null;
  getCurrentChatData: () => ChatData | null;
  updateChatMetadata: (
    chatId: string,
    updatedMetadata: Partial<ChatData>
  ) => void;
  addChatObject: (
    chatId: string,
    userQuestion: string,
    epicKey?: string | null,
    epicSummary?: string | null,
    projectName?: string | null,
    projectKeys?: ChatProjectKey[],
    dateRange?: string,
    issueUrl?: string | null,
    status?: string | null,
    created?: string | null,
    updated?: string | null,
    mode?: string,
    projectKey?: string | null
  ) => string;
  updateChatObject: (
    chatId: string,
    chatObjectId: string,
    updatedFields: Partial<ChatObject>
  ) => void;
  /** Remove a single message from a thread (used when retrying a failed message). */
  deleteChatObject: (chatId: string, chatObjectId: string) => void;
  /** Prepend older messages to the start of a conversation (used by scroll-up pagination). */
  prependChatObjects: (chatId: string, chatObjects: ChatObject[]) => void;
  /** Persist the current message page and whether more pages exist for a chat. */
  setChatPagination: (chatId: string, page: number, hasMore: boolean) => void;
  setInputValue: (newValue: string) => void;
  addToChatHistory: (
    chatId: string,
    userQuestion: string,
    aiResponse: string
  ) => void;
  clearChatHistory: (chatId: string) => void;
  clearChatConversation: (chatId: string) => void;
  deleteChat: (chatId: string) => void;
  clearAllChats: () => void;
  clearCurrentChat: () => void;
  clearCurrentConversation: () => void;
  clearCurrentChatHistory: () => void;
  getAllChats: () => AllChats;
  getChatsByProject: (projectName: string) => [string, ChatData][];
  getChatsByEpicKey: (epicId: string) => [string, ChatData][];
  getChatsByMode: (mode: string) => [string, ChatData][];
  getChatsByProjectKeys: (projectKeys: string[]) => [string, ChatData][];
  /** Rename a chat entry (e.g. temp UUID → real backend thread_id after first send). */
  renameChat: (oldId: string, newId: string) => void;
}

const useChatsStore = create<ChatsState>()(
  devtools(
    persist(
      (set, get) => ({
        allChats: {},
        epicChats: {},
        inputValue: '',
        selectedChatId: '',
        selectedChat: null,
        selectedChatEpic: '',
        powerModePayloadChatId: '',

        // Get the currently selected epic from insights store
        getSelectedEpic: () => {
          return useInsightsStore.getState().selectedEpic;
        },

        // Initialize chat structure
        initializeChat: (
          chatId = null,
          epicKey = null,
          epicSummary = null,
          projectName = null,
          projectKeys = [],
          dateRange = '',
          issueUrl = null,
          status = null,
          created = null,
          updated = null,
          mode = 'single',
          projectKey = null,
          projectCodeNames = []
        ) => {
          const newChatId = chatId || uuidv4();

          set((state) => {
            if (!state.allChats[newChatId]) {
              return {
                allChats: {
                  ...state.allChats,
                  [newChatId]: {
                    key: epicKey || newChatId,
                    summary: epicSummary || '',
                    projectName: projectName || '',
                    projectKey: projectKey || '',
                    projectKeys: projectKeys || [],
                    projectCodeNames: projectCodeNames || [],
                    dateRange: dateRange || '',
                    issueUrl: issueUrl || '',
                    status: status || '',
                    created: created || new Date().toISOString(),
                    updated: updated || new Date().toISOString(),
                    mode: mode || 'single',
                    conversation: [],
                    chat_history: [],
                  },
                },
              };
            }
            return state;
          });

          return newChatId;
        },

        updateSelectedChat: (selectedChat) =>
          set({
            selectedChat: selectedChat,
          }),
        updateSelectedChatEpic: (epic) =>
          set({
            selectedChatEpic: epic,
          }),

        updatePowerModePayloadChatId: (powerModePayloadChatId) =>
          set({
            powerModePayloadChatId: powerModePayloadChatId,
          }),

        // Update selected chat
        updateSelectedChatId: (chatId) =>
          set({
            selectedChatId: chatId,
          }),

        // Get conversation for specific chat
        getChatConversation: (chatId) => {
          const state = get();
          return state.allChats[chatId]?.conversation || [];
        },

        // Alias for getChatConversation
        getEpicConversation: (chatId) => {
          const state = get();
          return state.allChats[chatId]?.conversation || [];
        },

        // Get chat history for specific chat
        getChatHistory: (chatId) => {
          const state = get();
          return state.allChats[chatId]?.chat_history || [];
        },

        // Get full chat data (metadata + conversation + history)
        getChatData: (chatId) => {
          const state = get();
          return state.allChats[chatId] || null;
        },

        // Get chat metadata only
        getChatMetadata: (chatId) => {
          const state = get();
          const chat = state.allChats[chatId];
          if (!chat) return null;

          const { conversation, chat_history, ...metadata } = chat;
          return metadata;
        },

        // Get conversation for current selected chat (convenience method)
        getCurrentConversation: () => {
          const state = get();
          if (!state.selectedChatId) return [];
          return state.allChats[state.selectedChatId]?.conversation || [];
        },

        // Get chat history for current selected chat (convenience method)
        getCurrentChatHistory: () => {
          const state = get();
          if (!state.selectedChatId) return [];
          return state.allChats[state.selectedChatId]?.chat_history || [];
        },

        // Get current chat metadata (convenience method)
        getCurrentChatMetadata: () => {
          const state = get();
          if (!state.selectedChatId) return null;
          return get().getChatMetadata(state.selectedChatId);
        },

        // Get current chat data (convenience method)
        getCurrentChatData: () => {
          const state = get();
          if (!state.selectedChatId) return null;
          return state.allChats[state.selectedChatId] || null;
        },

        // Update chat metadata
        updateChatMetadata: (chatId, updatedMetadata) =>
          set((state) => {
            if (!state.allChats[chatId]) {
              return state;
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  ...updatedMetadata,
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Add chat object for specific chat
        addChatObject: (
          chatId,
          userQuestion,
          epicKey = null,
          epicSummary = null,
          projectName = null,
          projectKeys = [],
          dateRange = '',
          issueUrl = null,
          status = null,
          created = null,
          updated = null,
          mode = 'single',
          projectKey = null
        ) => {
          const newChatObject: ChatObject = {
            id: uuidv4(),
            threadId: null,
            userQuestion,
            aiResponse: null,
            timestamp: new Date().toISOString(),
            responseComplete: false,
          };

          set((state) => {
            // Initialize chat if it doesn't exist
            if (!state.allChats[chatId]) {
              state.allChats[chatId] = {
                key: epicKey || chatId,
                summary: epicSummary || '',
                projectName: projectName || '',
                projectKey: projectKey || '',
                projectKeys: projectKeys || [],
                projectCodeNames: [],
                dateRange: dateRange || '',
                issueUrl: issueUrl || '',
                status: status || '',
                created: created || new Date().toISOString(),
                updated: updated || new Date().toISOString(),
                mode: mode || 'single',
                conversation: [],
                chat_history: [],
              };
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  conversation: [
                    ...state.allChats[chatId].conversation,
                    newChatObject,
                  ],
                  updated: new Date().toISOString(),
                },
              },
            };
          });

          return newChatObject.id;
        },

        // Update chat object for specific chat
        updateChatObject: (chatId, chatObjectId, updatedFields) =>
          set((state) => {
            if (!state.allChats[chatId]) {
              return state;
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  conversation: state.allChats[chatId].conversation.map(
                    (chat) =>
                      chat.id === chatObjectId
                        ? { ...chat, ...updatedFields }
                        : chat
                  ),
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Remove a single chat object (message) from a thread
        deleteChatObject: (chatId, chatObjectId) =>
          set((state) => {
            if (!state.allChats[chatId]) return state;
            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  conversation: state.allChats[chatId].conversation.filter(
                    (c) => c.id !== chatObjectId
                  ),
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Prepend older messages to the start of a conversation
        prependChatObjects: (chatId, chatObjects) =>
          set((state) => {
            if (!state.allChats[chatId] || chatObjects.length === 0) {
              return state;
            }
            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  conversation: [
                    ...chatObjects,
                    ...state.allChats[chatId].conversation,
                  ],
                },
              },
            };
          }),

        // Track the latest fetched page and whether more pages exist
        setChatPagination: (chatId, page, hasMore) =>
          set((state) => {
            if (!state.allChats[chatId]) return state;
            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  messagesPage: page,
                  hasMoreMessages: hasMore,
                },
              },
            };
          }),

        // Set input value
        setInputValue: (newValue) =>
          set(() => ({
            inputValue: newValue,
          })),

        // Add chat to history with rolling buffer behavior for specific chat
        addToChatHistory: (chatId, userQuestion, aiResponse) =>
          set((state) => {
            // Initialize chat if it doesn't exist
            if (!state.allChats[chatId]) {
              state.allChats[chatId] = {
                key: chatId,
                summary: '',
                projectName: '',
                projectKeys: [],
                projectCodeNames: [],
                issueUrl: '',
                status: '',
                created: new Date().toISOString(),
                updated: new Date().toISOString(),
                mode: 'single',
                conversation: [],
                chat_history: [],
              };
            }

            const newHistoryItem: ChatHistoryItem = {
              userQuestion,
              aiResponse,
              timestamp: new Date().toISOString(),
            };

            let updatedHistory = [
              ...state.allChats[chatId].chat_history,
              newHistoryItem,
            ];

            // If we exceed 10 items, remove the oldest one
            if (updatedHistory.length > 10) {
              updatedHistory = updatedHistory.slice(1);
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  chat_history: updatedHistory,
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Clear chat history for specific chat
        clearChatHistory: (chatId) =>
          set((state) => {
            if (!state.allChats[chatId]) {
              return state;
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  chat_history: [],
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Clear entire conversation for specific chat
        clearChatConversation: (chatId) =>
          set((state) => {
            if (!state.allChats[chatId]) {
              return state;
            }

            return {
              allChats: {
                ...state.allChats,
                [chatId]: {
                  ...state.allChats[chatId],
                  conversation: [],
                  updated: new Date().toISOString(),
                },
              },
            };
          }),

        // Delete entire chat
        deleteChat: (chatId) =>
          set((state) => {
            const newAllChats = { ...state.allChats };
            delete newAllChats[chatId];

            const newSelectedChatId =
              state.selectedChatId === chatId ? '' : state.selectedChatId;

            return {
              allChats: newAllChats,
              selectedChatId: newSelectedChatId,
            };
          }),

        // Clear all chat data (for logout or reset)
        clearAllChats: () =>
          set(() => ({
            allChats: {},
            selectedChatId: '',
          })),

        // Clear current chat data (convenience method)
        clearCurrentChat: () => {
          const state = get();
          if (!state.selectedChatId) return;
          get().deleteChat(state.selectedChatId);
        },

        // Clear current chat conversation (convenience method)
        clearCurrentConversation: () => {
          const state = get();
          if (!state.selectedChatId) return;
          get().clearChatConversation(state.selectedChatId);
        },

        // Clear current chat history (convenience method)
        clearCurrentChatHistory: () => {
          const state = get();
          if (!state.selectedChatId) return;
          get().clearChatHistory(state.selectedChatId);
        },

        // Get all chats (for listing/management)
        getAllChats: () => {
          const state = get();
          return state.allChats;
        },

        // Get chats by project (helper method)
        getChatsByProject: (projectName) => {
          const state = get();
          return Object.entries(state.allChats).filter(
            ([, chat]) => chat.projectName === projectName
          );
        },
        // Get chats by epicId (helper method)
        getChatsByEpicKey: (epicId) => {
          const state = get();
          return Object.entries(state.allChats).filter(
            ([, chat]) => chat.key === epicId
          );
        },

        // Get chats by mode (helper method)
        getChatsByMode: (mode) => {
          const state = get();
          return Object.entries(state.allChats).filter(
            ([, chat]) => chat.mode === mode
          );
        },

        // Get chats by project keys (helper method)
        getChatsByProjectKeys: (projectKeys) => {
          const state = get();
          return Object.entries(state.allChats).filter(([, chat]) =>
            chat.projectKeys.some((key) => projectKeys.includes(key.key))
          );
        },

        // Rename a chat entry — used to migrate a temp UUID to the backend thread_id
        renameChat: (oldId, newId) =>
          set((state) => {
            if (!state.allChats[oldId] || oldId === newId) return state;

            const chat = state.allChats[oldId];
            const newAllChats = { ...state.allChats };
            delete newAllChats[oldId];
            newAllChats[newId] = { ...chat, key: newId };

            return {
              allChats: newAllChats,
              selectedChatId:
                state.selectedChatId === oldId ? newId : state.selectedChatId,
            };
          }),
      }),
      {
        name: 'chats',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    {
      name: 'ChatsStore',
    }
  )
);

export default useChatsStore;
